import React, {useState} from 'react';
import {
    Text,
    View,
    Image
} from 'react-native';

import {styles} from "./InvestFlowSubscriptionConfirmationViewStyle";
import InvestFlowTouchableButtonView from "../touchablebutton/InvestFlowTouchableButtonView";
import InvestmentsService from "../../../../../Proxy/InvestmentsService";
import {NewInvestment} from "../../../../../Common/DTO/Investment";
import InvestFlowHeaderView from "../investflowheader/InvestFlowHeaderView";
import User from "../../../../../Common/DTO/User";
import Project from "../../../../../Common/DTO/Project";
import {Application, LanguageManager, Managers, NavigationManager, TranslationManager} from "@levelapp/softfabric";
import {TranslationView} from "@levelapp/softfabric-ui";
import App from "../../../../../App";
import {UserElement} from "../../InvestFlowView";

interface InvestFlowSubscriptionConfirmationViewProps {
    user: User | null;
    setShowResult: (value: boolean) => void;
    amount: number;
    project: Project | null;
    selectedUser: UserElement|null;
}

interface PaymentResponse {
    iban: string;
    amount: number;
    communication: string;
}

export const InvestFlowSubscriptionConfirmationView = ({user, setShowResult, amount, project, selectedUser}: InvestFlowSubscriptionConfirmationViewProps) => {

    const translationManager: TranslationManager = App.current.resolve<TranslationManager>(Managers.TRANSLATION_MANAGER);
    const languageManager: LanguageManager = App.current.resolve<LanguageManager>(Managers.LANGUAGE_MANAGER);

    const qrCodeRef = React.useRef<string | null>(null);
    const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
    const paymentResponseRef = React.useRef<PaymentResponse | null>(null);

    function getRetractionFullEndDate() {
        const options: Intl.DateTimeFormatOptions = { weekday: 'long', day: '2-digit', month: 'long', hour: '2-digit', minute: '2-digit' };

        const retractionDaysTimeout = 4;
        const futureDate = new Date();
        futureDate.setDate(futureDate.getDate() + retractionDaysTimeout);

        return futureDate.toLocaleDateString('fr-FR', options).replace(',', ' à');
    }

    function checkCurrentTicket() {
        if (!project) return false;

        const parts: string[] = project.issueDate.split("/");
        if (parts.length === 3) {
            project.issueDate = `${parts[2]}-${parts[1]}-${parts[0]}`;
        }

        const issueDate = new Date(project.issueDate);

        // @ts-ignore
        if (isNaN(issueDate)) {
            console.error("Invalid Date detected!");
            return false;
        }

        const nowDate = new Date();

        return issueDate < nowDate;
    }
    const investmentsService = new InvestmentsService();

    const [showCurrentTicket, setShowCurrentTicket] = useState<boolean>(false);
    const [showInvestment, setShowInvestment] = useState<boolean>(false);

    function onCancelPress() {
        setShowResult(false);
    }

    async function onConfirmPress() {
        const investment: NewInvestment = {
            amount : amount,
            projectCode : project?.code ?? "",
            source : "WEBSITE",
            legalEntityId: selectedUser && selectedUser.isLegalEntity ? selectedUser.id : undefined
        };

        await investmentsService.createOne(investment, (response: any) => {
            if (response?.qrCode) {
                qrCodeRef.current = response.qrCode;
                paymentResponseRef.current = response.payment;
                if (paymentResponseRef.current?.amount) {
                    paymentResponseRef.current.amount = paymentResponseRef.current.amount * 1000;
                }
                
                const byteCharacters = atob(response.qrCode);
                const byteNumbers = Array.from(byteCharacters).map(c => c.charCodeAt(0));
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], { type: 'image/png' });
                const url = URL.createObjectURL(blob);
                setQrCodeUrl(url);
            }
            if (checkCurrentTicket()) {
            setShowCurrentTicket(true);
            return;
        }
        setShowInvestment(true);
        });

        if ((window as any).dataLayer) {
            (window as any).dataLayer.push({
              event: 'investment',
              projectName: project?.code ?? "",
              montant: amount,
            });
          }

        
    }

    function onProjectPagePress() {
        const code = project?.code;
        Application.current?.resolve<NavigationManager>(Managers.NAVIGATION_MANAGER)?.navigate(`/details/${code}`);
    }

    function translateKeyWithData(key: string, data: any): string {
        return translationManager.translate(key, languageManager.language).replace("${data}", data.toString())
    }

    return (
        <View style={[styles.page]}>
            {showCurrentTicket && (
                <>
                    <InvestFlowHeaderView titleTranslationKey="investflow.subscription.confirmation.thanksForInvesting"/>
                    <Text style={[styles.subTitle]}>{translateKeyWithData("investflow.subscription.confirmation.currentTicket", project?.issueDate ?? "date inconnue")}</Text>
                    <View style={{height: 15}}/>
                    {qrCodeUrl && (
                       <View>
                            <TranslationView style={[styles.subTitle]}>investflow.subscription.qrCode</TranslationView>
                            <Image 
                                source={{ uri: qrCodeUrl }}
                                style={{ width: 200, height: 200 }}
                            />
                        </View>
                    )}
                    <TranslationView style={[styles.subTitle]}>investflow.subscription.confirmation.currentTicketThanks</TranslationView>
                    <View style={{height: 15}}/>
                    <InvestFlowTouchableButtonView pressableButtonStyle={{backgroundColor: "BEE_YELLOW"}} translationKey="investflow.button.go.back.details" action={onProjectPagePress}/>
                </>
            )}
            {showInvestment && (
                <>
                    <InvestFlowHeaderView titleTranslationKey="investflow.subscription.confirmation.thanksForInvesting"/>
                    <Text style={[styles.subTitle]}>{translateKeyWithData("investflow.subscription.confirmation.lastStep", user?.email)}</Text>
                    <View style={{height: 15}}/>
                    {qrCodeUrl && (
                       <View>
                            <TranslationView style={[styles.subTitle]}>investflow.subscription.qrCode</TranslationView>
                            <Image 
                                source={{ uri: qrCodeUrl }}
                                style={{ width: 200, height: 200 }}
                            />
                        </View>
                    )}
                    <Text style={[styles.subTitle]}>{translateKeyWithData("investflow.subscription.confirmation.retractionPeriod", getRetractionFullEndDate())}</Text>
                    <View style={{height: 15}}/>
                    <InvestFlowTouchableButtonView pressableButtonStyle={{backgroundColor: "BEE_YELLOW"}} translationKey="investflow.button.go.back.details" action={onProjectPagePress}/>
                </>
            )}
            {!showInvestment && !showCurrentTicket && (
                <>
                    <Text style={[styles.title]}>{translateKeyWithData("investflow.subscription.confirmation.subscriptionConfirm", amount)}</Text>
                    <TranslationView style={[styles.subTitle]}>investflow.subscription.confirmation.subscriptionRefraction</TranslationView>
                    <View style={[styles.buttonsContainer]}>
                        <InvestFlowTouchableButtonView pressableButtonStyle={{backgroundColor: "DUCK_BLUE", marginRight: 20}} translationKey="investflow.button.cancel" action={onCancelPress}/>
                        <InvestFlowTouchableButtonView pressableButtonStyle={{backgroundColor: "BEE_YELLOW"}} translationKey="investflow.button.validate" action={onConfirmPress}/>
                    </View>
                </>
            )}

        </View>
    );
}

export default InvestFlowSubscriptionConfirmationView;